import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { roundToCents } from "@common/helpers/monetary";
import { getDefaultAccrualAccountId } from "@common/helpers/accrualAccount";
import { Account } from "@common/types/domains/accounting/account";
import { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import {
    getEffectiveAccrualLedgerAccountId,
    getEffectiveGeneralLedgerAccountId,
    isMatchedToInvoiceOrBill,
} from "@common/helpers/transactions";
import { buildPrimaryLedgerOverride } from "@lib/accountOverrides";
import { isAccrualLedgerDocumentAccount } from "../matchDrawerDefaults";
import { MatchableItem, MatchableTransaction, MatchItemType } from "../types";
import { useRemainingLineSetters } from "./useRemainingLineSetters";

export interface RemainingLine {
    id: string;
    accrualAccountId: string | null;
    cashAccountId: string | null;
    classIds: string[];
    categoryId: string | null;
    amount: number;
    description: string;
}

interface UseRemainingLinesParams {
    sourceTransaction: MatchableTransaction | null;
    defaultAccountId: string | null;
    defaultCashAccountId: string | null;
    financialAccountId: number | null;
    sourceClassIds: string[];
}

export function resolvePreMatchLedgerAccountIds({
    transaction,
    entityId,
    accounts,
}: {
    transaction: MatchableTransaction;
    entityId: number;
    accounts: Account[];
}): { cashAccountId: string | null; accrualAccountId: string | null } {
    const journalEntries = isMatchedToInvoiceOrBill(
        transaction.transactionMatches,
    )
        ? null
        : transaction.journalEntries;
    const cashAccountId = getEffectiveGeneralLedgerAccountId({
        amount: transaction.amount,
        accountId: transaction.accountId ?? null,
        entityId,
        journalEntries,
    });
    const assignedAccrualAccountId = getEffectiveAccrualLedgerAccountId({
        amount: transaction.amount,
        accrualAccountId: transaction.accrualAccountId ?? null,
        entityId,
        journalEntries,
    });
    const preMatchAccrualAccountId = [
        assignedAccrualAccountId,
        cashAccountId,
    ].find(
        (accountId) =>
            !!accountId &&
            !isAccrualLedgerDocumentAccount(
                accounts.find((account) => account.id === accountId),
            ),
    );

    return {
        cashAccountId,
        accrualAccountId:
            preMatchAccrualAccountId ??
            getDefaultAccrualAccountId({
                transaction,
                accounts,
                ignoreMatches: true,
            }),
    };
}

export function resolveRemainingLineDefaults({
    transaction,
    entityId,
    accounts,
}: {
    transaction: MatchableTransaction;
    entityId: number | undefined;
    accounts: Account[];
}): {
    cashAccountId: string | null;
    accrualAccountId: string | null;
    classIds: string[];
} {
    return {
        ...resolvePreMatchLedgerAccountIds({
            transaction,
            entityId: entityId ?? transaction.entity.id,
            accounts,
        }),
        classIds:
            transaction.classAssignments?.map(({ classId }) => classId) ?? [],
    };
}

export function buildRemainingAccountOverrides({
    accrualLedgerId,
    accrualAccountId,
    cashLedgerId,
    cashAccountId,
}: {
    accrualLedgerId: string | undefined;
    accrualAccountId: string;
    cashLedgerId: string | undefined;
    cashAccountId: string | null;
}): AccountOverrideEntryRequest[] {
    return [
        ...buildPrimaryLedgerOverride(accrualLedgerId, accrualAccountId),
        ...(cashAccountId
            ? buildPrimaryLedgerOverride(cashLedgerId, cashAccountId)
            : []),
    ];
}

export function createRemainingLine({
    sourceTransaction,
    accrualAccountId,
    cashAccountId,
    classIds,
    amount,
}: {
    sourceTransaction: MatchableTransaction | null;
    accrualAccountId: string | null;
    cashAccountId: string | null;
    classIds: string[];
    amount: number;
}): RemainingLine {
    return {
        id: uuidv4(),
        accrualAccountId,
        cashAccountId,
        classIds,
        categoryId: sourceTransaction?.categoryId ?? null,
        amount,
        description: sourceTransaction?.bankDescription ?? "",
    };
}

export function remainingLineToMatchableItem({
    line,
    sourceTransaction,
    financialAccountId,
    ledgerEntityId,
}: {
    line: RemainingLine;
    sourceTransaction: MatchableTransaction;
    financialAccountId: number | null;
    ledgerEntityId?: number;
}): MatchableItem {
    return {
        id: line.id,
        date: sourceTransaction.date,
        type: MatchItemType.REMAINING,
        counterpartyName: sourceTransaction.counterparty?.name ?? null,
        description: line.description,
        accountIds: line.accrualAccountId ? [line.accrualAccountId] : [],
        accrualAccountIds: line.accrualAccountId ? [line.accrualAccountId] : [],
        cashAccountIds: line.cashAccountId ? [line.cashAccountId] : [],
        classIds: line.classIds,
        categoryId: line.categoryId,
        amount: line.amount,
        openAmount: null,
        entityId: sourceTransaction.entity.id,
        entity: sourceTransaction.entity,
        financialAccountId,
        ...(ledgerEntityId !== undefined && { ledgerEntityId }),
    };
}

export function useRemainingLines({
    sourceTransaction,
    defaultAccountId,
    defaultCashAccountId,
    financialAccountId,
    sourceClassIds,
}: UseRemainingLinesParams) {
    const [isVisible, setIsVisible] = useState(false);
    const [lines, setLines] = useState<RemainingLine[]>([]);
    const sourceTransactionId = sourceTransaction?.id ?? null;

    useEffect(() => {
        setLines([]);
        setIsVisible(false);
    }, [sourceTransactionId]);

    const createLine = useCallback(
        (amount: number): RemainingLine =>
            createRemainingLine({
                sourceTransaction,
                accrualAccountId: defaultAccountId,
                cashAccountId: defaultCashAccountId,
                classIds: sourceClassIds,
                amount,
            }),
        [
            defaultAccountId,
            defaultCashAccountId,
            sourceClassIds,
            sourceTransaction,
        ],
    );

    const show = useCallback(
        (prefillAmount: number) => {
            setIsVisible(true);
            setLines((prev) => {
                if (prev.length > 0) {
                    return prev;
                }
                return [createLine(Math.max(0, prefillAmount))];
            });
        },
        [createLine],
    );

    const addLine = useCallback(() => {
        setLines((prev) => [...prev, createLine(0)]);
    }, [createLine]);

    const removeLine = useCallback((id: string) => {
        setLines((prev) => {
            const filtered = prev.filter((line) => line.id !== id);
            if (filtered.length === 0) {
                setIsVisible(false);
            }
            return filtered;
        });
    }, []);

    const setters = useRemainingLineSetters(setLines);

    const reset = useCallback(() => {
        setLines([]);
        setIsVisible(false);
    }, []);

    const total = useMemo(
        () =>
            roundToCents(
                lines
                    .filter((line) => line.accrualAccountId)
                    .reduce((sum, line) => sum + line.amount, 0),
            ),
        [lines],
    );

    const items = useMemo<MatchableItem[]>(() => {
        if (!sourceTransaction) {
            return [];
        }
        return lines.map((line) =>
            remainingLineToMatchableItem({
                line,
                sourceTransaction,
                financialAccountId,
            }),
        );
    }, [financialAccountId, lines, sourceTransaction]);

    return {
        isVisible,
        lines,
        items,
        total,
        show,
        addLine,
        removeLine,
        ...setters,
        reset,
    };
}
